// Written content for each program.
//
// This is the ONLY part of a course page that has to be typed by hand —
// year levels, term structure, timetables and related courses are all
// generated. Everything here has to come from the actual brochure or
// curriculum: a course page that promises modules we do not teach is worse
// than a course page with a section missing.
//
// Any field left null is simply not rendered. Fill them in as the brochures
// and timetables arrive.
//
//   tagline  one line under the page title
//   intro    a short paragraph or two of overview
//   covered  { note?, modules: [{ title, items: [] }] }  — "What's covered"
//   audience [] — who the course suits
//   faqs     [{ q, a }] — appended to the shared FAQ list

export const PROGRAM_CONTENT = {
  wrmt: {
    tagline:
      "Our comprehensive primary program: Writing, Reading skills, Mathematical reasoning skills and Thinking skills in one course.",
    intro:
      "WRMT is the core weekly program for primary students. Rather than teaching one subject in isolation, it works across the whole week of school work: Writing, Reading skills, Mathematical reasoning skills and Thinking skills (the four letters of WRMT). Year 6 takes WRMT in Term 1, then WEM in Terms 2 and 3.",
    covered: null, // awaiting curriculum
    audience: null,
    faqs: null,
  },

  "oc-preliminary-trial-tests": {
    tagline:
      "A year ahead of the Opportunity Class test, so Year 4 is practice rather than panic.",
    intro:
      "The OC placement test is sat in Year 4 for entry in Year 5. Students who meet the format for the first time in the exam year spend most of it learning how the test works instead of getting better at it. This preliminary course puts that year back.",
    covered: {
      note: "Built around the three sections of the real OC placement test, at Year 3 level.",
      modules: [
        {
          title: "Reading — 40 minutes",
          items: [
            "Non-fiction, fiction, poetry, magazine articles and reports",
            "Multi-part questions, where one passage carries several answers",
            "Reading for the answer under time pressure, not reading for pleasure",
          ],
        },
        {
          title: "Mathematical Reasoning — 40 minutes",
          items: [
            "Applying what is known to problems that look unfamiliar",
            "Five-option multiple choice, worked without a calculator",
            "Mental strategies and estimation, so time is not lost on arithmetic",
          ],
        },
        {
          title: "Thinking Skills — 30 minutes",
          items: [
            "Critical thinking and problem solving with no assumed knowledge",
            "Logic puzzles, sequences and identifying the flaw in an argument",
            "Four-option multiple choice, the section most students have never seen",
          ],
        },
        {
          title: "Test conditions",
          items: [
            "Sat on computer, as the real test is",
            "Every incorrect question reviewed online with a video explanation",
            "Results reported back so strengths and weaknesses are visible early",
          ],
        },
      ],
    },
    audience: null,
    faqs: [
      {
        q: "When is the OC test actually sat?",
        a: "In Year 4, for entry to an Opportunity Class in Year 5. This Year 3 course is preparation for that.",
      },
    ],
  },

  "oc-trial-tests": {
    tagline:
      "Full trial papers under real test conditions, in the year your child sits the OC test.",
    intro:
      "The Opportunity Class placement test is three computer-based sections in one sitting, each worth a third of the mark. This course runs full trial papers matched to that structure, marked and reviewed, so nothing on the day is new.",
    covered: {
      note: "Each trial paper mirrors the real test: three sections, equally weighted, sat on computer.",
      modules: [
        {
          title: "Reading — 14 questions, 40 minutes, 33.3%",
          items: [
            "Three questions carry multiple parts, for 33 answers in total",
            "Non-fiction, fiction, poetry, magazine articles and reports",
            "Pacing: roughly a minute per answer, with no time to re-read",
          ],
        },
        {
          title: "Mathematical Reasoning — 35 questions, 40 minutes, 33.3%",
          items: [
            "Five-option multiple choice, no calculator permitted",
            "Applying mathematical understanding to problems not seen before",
            "Drawn from the NSW curriculum to Year 4 — no extra content required",
          ],
        },
        {
          title: "Thinking Skills — 30 questions, 30 minutes, 33.3%",
          items: [
            "Four-option multiple choice testing critical thinking and problem solving",
            "No prior knowledge assumed, which is exactly why it needs practice",
            "The section where prepared students gain the most ground",
          ],
        },
        {
          title: "After every paper",
          items: [
            "Online review of every incorrect question, with video explanations",
            "Immediate feedback on strengths and weaknesses",
            "Speed and accuracy tracked across papers, not just a score",
          ],
        },
      ],
    },
    audience: null,
    faqs: null,
  },

  "selective-preliminary-trial-tests": {
    tagline:
      "A year of preparation before the Selective test year, including the writing section most courses leave late.",
    intro:
      "The Selective High School placement test is sat in Year 6 and has four equally weighted sections — including a 30-minute writing task that cannot be crammed. Starting in Year 5 turns the exam year into practice instead of catch-up.",
    covered: {
      note: "The four sections of the real Selective test, introduced at Year 5 level.",
      modules: [
        {
          title: "Reading — 45 minutes",
          items: [
            "Non-fiction, fiction, poetry, magazine articles and reports",
            "Multi-part questions where one text carries several answers",
            "Working out what the question is really asking before answering",
          ],
        },
        {
          title: "Mathematical Reasoning — 40 minutes",
          items: [
            "Five-option multiple choice, no calculator",
            "Reasoning through unfamiliar problems rather than recalling a method",
            "Estimation and elimination as timing strategies",
          ],
        },
        {
          title: "Thinking Skills — 40 minutes",
          items: [
            "Four-option multiple choice, no assumed knowledge",
            "Logical reasoning, testing assumptions, evaluating arguments",
            "The section that is hardest to improve without regular practice",
          ],
        },
        {
          title: "Writing — 30 minutes",
          items: [
            "One open-response task on a given topic",
            "Marked on the ideas, on writing for a purpose and audience, and on use of language for effect",
            "Written to time, marked, and rewritten — the only way this section improves",
          ],
        },
      ],
    },
    audience: null,
    faqs: null,
  },

  "selective-trial-tests": {
    tagline:
      "Full trial papers matched to the real Selective test, section for section.",
    intro:
      "The Selective High School placement test is four sections in one day on computer, each worth a quarter of the mark. This course runs full trial papers built to that structure, sat under test conditions, marked and reviewed question by question.",
    covered: {
      note: "Every trial paper matches the real test: four sections, equally weighted, one sitting.",
      modules: [
        {
          title: "Reading — 17 questions, 45 minutes, 25%",
          items: [
            "Three questions carry multiple parts",
            "Non-fiction, fiction, poetry, magazine articles and reports",
            "Pacing practised until finishing the section stops being the problem",
          ],
        },
        {
          title: "Mathematical Reasoning — 35 questions, 40 minutes, 25%",
          items: [
            "Five-option multiple choice, no calculator permitted",
            "Mathematical reasoning applied to unfamiliar problems",
            "Roughly 68 seconds per question, so method selection matters more than method",
          ],
        },
        {
          title: "Thinking Skills — 40 questions, 40 minutes, 25%",
          items: [
            "Four-option multiple choice, no prior knowledge required",
            "Critical thinking, logical reasoning and problem solving",
            "The largest section by question count, and the most trainable",
          ],
        },
        {
          title: "Writing — 1 task, 30 minutes, 25%",
          items: [
            "One open response, which must address the topic given",
            "Assessed on the creativity of the ideas, writing for a purpose and audience, and use of language for effect",
            "A quarter of the total mark decided in half an hour",
          ],
        },
        {
          title: "After every paper",
          items: [
            "Online review of every incorrect question, with video explanations",
            "Test environment set up to mirror the department's own conditions",
            "Immediate feedback on strengths and weaknesses, tracked paper to paper",
          ],
        },
      ],
    },
    audience: null,
    faqs: [
      {
        q: "How many trial tests are there?",
        a: "The full Selective Trial Test Course runs 51 tests across five terms, finishing with a final trial before the real test.",
      },
    ],
  },

  "hd-class": {
    tagline: null, // we have no description for HD Class yet — do not invent one
    intro: null,
    covered: null,
    audience: null,
    faqs: null,
  },

  wem: {
    tagline:
      "Writing, English and Mathematics for Year 6, in Terms 2 and 3.",
    intro:
      "WEM follows WRMT in Year 6. From Term 2 the program narrows to the three subjects that carry into high school: Writing, English and Mathematics, taught to the Year 6 standard.",
    covered: null,
    audience: null,
    faqs: null,
  },

  "high-school-preparation": {
    tagline:
      "Term 4 of Year 6: English & Writing, Mathematics and Science, a term ahead of Year 7.",
    intro:
      "In Term 4, Year 6 students start high school work a term early. High School Preparation is the three high school subjects (English & Writing, Mathematics and Science) taught as our Y6/7 classes at the high school campuses, so Year 7 starts with a term of it already done.",
    covered: null, // awaiting curriculum
    audience: null,
    faqs: null,
  },

  "english-and-writing": {
    tagline:
      "Reading, analysis and writing, taught to the standard expected at each year level.",
    intro:
      "A weekly program covering text analysis, comprehension and writing. Work is set for the specific year level rather than a general ability band, so it lines up with what students are doing at school.",
    covered: null, // awaiting curriculum
    audience: null,
    faqs: null,
  },

  maths: {
    tagline:
      "Mathematics taught to the year level, with the reasoning practised alongside the method.",
    intro:
      "A weekly program working through the mathematics of each year level, with time spent on why a method works rather than only how to apply it.",
    covered: null, // awaiting curriculum
    audience: null,
    faqs: null,
  },

  science: {
    tagline:
      "Science for Years 7 to 10, building the knowledge and the scientific thinking behind it.",
    intro:
      "A weekly program covering the science curriculum for each year level, with emphasis on understanding the reasoning as well as the content.",
    covered: null, // awaiting curriculum
    audience: null,
    faqs: null,
  },

  // ---- Senior years -----------------------------------------------------
  // Written from our own Norwest senior timetable and how the HSC is
  // structured. Nothing here is lifted from another college's site: their
  // copy describes their programs, not ours.

  "hsc-english": {
    tagline:
      "HSC English, worked through the modules and the writing they demand.",
    intro:
      "English is the only subject every HSC student sits, and it carries into the ATAR for all of them. The course works through the prescribed modules and the kinds of response each one asks for, with writing marked and returned so students see what is costing them marks before the exam does.",
    covered: null,
    audience: null,
    faqs: null,
  },

  "maths-advanced": {
    tagline: "Mathematics Advanced (2 Unit) for Year 11 and Year 12.",
    intro:
      "The 2 Unit course, taught so that students can see why a method works rather than only reproduce it. Time is spent on the question types that carry the most marks in the HSC paper.",
    covered: null,
    audience: null,
    faqs: null,
  },

  "maths-extension-1": {
    tagline: "Mathematics Extension 1 (3 Unit), alongside the Advanced course.",
    intro:
      "Extension 1 builds on Mathematics Advanced and is taken with it. The pace assumes the Advanced content is in place and moves into the harder proof and application work the extension paper expects.",
    covered: null,
    audience: null,
    faqs: null,
  },

  "maths-extension-2": {
    tagline: "Mathematics Extension 2 (4 Unit), for Year 12.",
    intro:
      "The most demanding mathematics course in the HSC, taken in Year 12 on top of Extension 1. The work is closer to first-year university mathematics than to the rest of the school syllabus.",
    covered: null,
    audience: null,
    faqs: null,
  },

  physics: {
    tagline: "HSC Physics for Year 11 and Year 12.",
    intro:
      "Physics rewards students who can apply a small number of principles to unfamiliar situations. The course works through the syllabus modules with that in mind, including the extended-response writing the paper asks for.",
    covered: null,
    audience: null,
    faqs: null,
  },

  chemistry: {
    tagline: "HSC Chemistry for Year 11 and Year 12.",
    intro:
      "Chemistry carries a heavy content load and a demanding calculation component. The course covers both, with the working set out the way markers expect to see it.",
    covered: null,
    audience: null,
    faqs: null,
  },

  biology: {
    tagline: "HSC Biology for Year 11 and Year 12.",
    intro:
      "Biology looks approachable and is often under-prepared for. The course keeps the content organised and puts the time into the extended responses, where most of the marks are won or lost.",
    covered: null,
    audience: null,
    faqs: null,
  },
};

export const contentFor = (slug) => PROGRAM_CONTENT[slug] || {};

// The 7 Step Learning System.
//
// Primary and high school run DIFFERENT seven-step cycles — this file used to
// carry one blended list, which described neither. Both are taken straight
// from our own brochures.

export const PRIMARY_STEPS = [
  {
    name: "Assessment Tests",
    body: "We find out where the student actually is before teaching anything, so the work is set at the right level from week one.",
    assessment: true,
  },
  {
    name: "Class Review (Lecture)",
    body: "The week's teaching, covering the new material and reviewing what came before it.",
  },
  {
    name: "Self-Directed Study",
    body: "Set reading and practice the student works through in their own time.",
  },
  {
    name: "Formative Assessment",
    body: "Weekly homework, marked and returned, showing whether the topic has landed while there is still time to do something about it.",
    assessment: true,
  },
  {
    name: "Small Group Tutoring",
    body: "A small group works through whatever the week's homework exposed — in class or online through Core Tutoring.",
  },
  {
    name: "Consulting",
    body: "A direct conversation when a student needs something tutoring has not fixed.",
  },
  {
    name: "Summative Assessment (Class Test)",
    body: "An end-of-term class test showing what has actually been retained, which sets the next term's starting point.",
    assessment: true,
  },
];

export const HIGH_SCHOOL_STEPS = [
  {
    name: "Assessment",
    body: "A diagnostic test shows current ability and which topics need more time. Formative assessment then runs through the term, with a summative assessment before final exams.",
    assessment: true,
  },
  {
    name: "Theory Classes",
    body: "Understanding, applying and memorising the core concepts, with worked examples aimed at how exam questions are actually asked.",
  },
  {
    name: "Homework",
    body: "Three levels in every set: basic questions for understanding, harder questions for application and exam technique, and extension questions for full command of the topic.",
  },
  {
    name: "Quizzes",
    body: "Assessed weekly in class so students keep their work current, and so teachers can see where each student is weak.",
    assessment: true,
  },
  {
    name: "Topic Tests",
    body: "Every topic is tested in class before moving on, with trial exams added ahead of school exams.",
    assessment: true,
  },
  {
    name: "Feedback",
    body: "Given in every tutorial, based on the student's own work booklets, quizzes and tests.",
  },
  {
    name: "Intensive Tutorial",
    body: "Targeted at what the student needs — basic concepts, problem-solving technique, extension work, and open discussion so students learn from each other.",
  },
];

// Kept for anything still importing the old name.
export const TERM_STAGES = PRIMARY_STEPS;

export const stepsForStage = (stage) =>
  stage === "primary" ? PRIMARY_STEPS : HIGH_SCHOOL_STEPS;

// What sets CS apart, in the order we would say it out loud.
export const CORE_STRENGTHS = [
  {
    title: "Our courses",
    body: "Every course is written in-house for a specific year level, so it lines up with what a student is doing at school rather than a general ability band.",
  },
  {
    title: "Our teachers",
    body: "Classes are taught by experienced teachers, kept small enough that they can tell who has understood a topic and who has not.",
  },
  {
    title: "Our material",
    body: "We write and publish our own course books and assessments. When a cohort struggles with something, we change the material instead of working around it.",
  },
];

// Extra questions that only come up for Year 11 and 12.
export const SENIOR_FAQS = [
  {
    q: "Do you run a free tutorial?",
    a: "Yes. There is a free tutorial every Saturday where students can bring homework and anything from the week they did not follow.",
  },
  {
    q: "Can we get one-to-one help as well?",
    a: "Yes. Private one-on-one tutoring runs alongside the classes for students who need something dealt with individually.",
  },
  {
    q: "Which maths course should my child take?",
    a: "Extension 1 is taken together with Mathematics Advanced, and Extension 2 is taken in Year 12 on top of Extension 1. Talk to us about the combination that fits your child's school pattern.",
  },
];

// Questions we get about every program.
export const SHARED_FAQS = [
  {
    q: "Can my child join partway through a term?",
    a: "Usually yes. Contact us with your child's year level and we will tell you what is running and whether there is room in the current term.",
  },
  {
    q: "Do you teach online as well as on campus?",
    a: "Yes. The material, the teachers and the assessments are the same either way, and students can move between the two.",
  },
  {
    q: "How do I know which level is right?",
    a: "Start with the free assessment. It shows where your child is sitting, and our team will talk you through which class fits.",
  },
];

// ---------------------------------------------------------------------------
// What students study in the comprehensive primary course, by year level.
//
// WRMT is an initialism: Writing, Reading skills, Mathematical reasoning
// skills, Thinking skills. The course is WRMT in Years 1-5 and WEM in Year 6
// (the old WEMG name, with General Ability, is retired) — see labelFor() in
// catalogue.js.
//
// Written from CS Curriculum_Primary. Each year genuinely differs: reading
// becomes a comprehension strategy from Year 3, maths splits into Essential
// and Enrich streams from Year 2, and persuasive writing starts at Year 3.
//
// Thinking Skills is deliberately thin — it is not in the curriculum document
// we have, so the copy stays general rather than inventing topics.
// ---------------------------------------------------------------------------

const thinking = (body) => ({ title: "Thinking Skills", body });

const THINKING_LOWER = thinking(
  "General ability work: patterns, sequences and simple logic puzzles that build the reasoning the other three subjects rely on."
);
const THINKING_UPPER = thinking(
  "Problem solving and critical thinking: analysing information, reasoning logically through an argument and testing the assumptions behind it."
);

export const PRIMARY_STUDY = {
  1: [
    {
      title: "Writing",
      body: "Students write narratives, recounts, procedures, descriptions, explanations and poetry, learning the shape of each text type before writing one of their own.",
    },
    {
      title: "English",
      body: "Reading built on four habits — getting the facts, making inferences, drawing conclusions and spelling — worked through narratives, information reports, fables and poems. Grammar, punctuation and letter sounds are covered every unit.",
    },
    {
      title: "Mathematics",
      body: "Essential Mathematics: addition and subtraction facts to fluency, then fractions, time, length and general problem solving.",
    },
    THINKING_LOWER,
  ],
  2: [
    {
      title: "Writing",
      body: "The Year 1 text types plus information reports and exposition, so students begin writing to inform and to argue as well as to tell a story.",
    },
    {
      title: "English",
      body: "A first novel study runs alongside comprehension work, and students meet proverbs and idioms. Grammar, punctuation, vocabulary and spelling continue every unit.",
    },
    {
      title: "Mathematics",
      body: "Essential Mathematics adds multiplication, division, capacity, mass and position. Enrich Mathematics starts here, with Advanced, Extension and Elite problem sets for students who want harder work.",
    },
    THINKING_LOWER,
  ],
  3: [
    {
      title: "Writing",
      body: "Persuasive writing joins the program this year, alongside narrative, recount, description, procedure, explanation, information report and poetry.",
    },
    {
      title: "Reading Skills",
      body: "Comprehension becomes a taught strategy rather than a set of questions, applied to narratives, information reports, explanations, poetry and cloze passages, with grammar, punctuation, vocabulary and spelling throughout.",
    },
    {
      title: "Mathematics",
      body: "Essential Mathematics covers numeration, rounding, multiplication, division, fractions and perimeter. Enrich Mathematics extends to Olympian and Level 4 problem sets.",
    },
    THINKING_UPPER,
  ],
  4: [
    {
      title: "Writing",
      body: "Persuasive and expository writing take a larger share, with narrative, recount, description, information report, explanation and poetry continuing.",
    },
    {
      title: "Reading Skills",
      body: "Comprehension strategy applied to novels and novel extracts as well as shorter texts, with cloze passages, poetry and information reports. Grammar, punctuation, vocabulary and spelling run every unit.",
    },
    {
      title: "Mathematics",
      body: "Essential Mathematics moves into decimals, time and data alongside the four operations. Enrich Mathematics runs Advanced through to Olympian problem sets.",
    },
    THINKING_UPPER,
  ],
  5: [
    {
      title: "Writing",
      body: "Persuasive, expository and information writing at the length and structure expected in selective and scholarship testing, with narrative and description maintained.",
    },
    {
      title: "Reading Skills",
      body: "Novel extracts and book reviews join the comprehension strategy work, pushing students from finding answers to forming a judgement about a text.",
    },
    {
      title: "Mathematics",
      body: "Essential Mathematics covers decimals, equivalent fractions, percentages, measurement and the area of rectangles and triangles. Enrich Mathematics adds multiple-choice exercises under time pressure.",
    },
    THINKING_UPPER,
  ],
  6: [
    {
      title: "Writing",
      body: "Persuasive and narrative writing at high-school entry standard, written to a plan and to time.",
    },
    {
      title: "Reading Skills",
      body: "Whole novels and author studies, with book reviews and extended comprehension responses — the closest primary work gets to Year 7 English.",
    },
    {
      title: "Mathematics",
      body: "Essential Mathematics covers chance, 3D objects, directions on a map and scale drawings. Enrich Mathematics continues through Advanced, Elite and Olympian multiple-choice sets.",
    },
    THINKING_UPPER,
  ],
};

export const studyFor = (year) => PRIMARY_STUDY[Number(year)] || null;
